import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API, COURSES, LEARNING_MODES, ROLES, YEAR_LEVELS } from "@/lib/site";

const schema = z.object({
  name: z.string().trim().min(2, "Please enter your name").max(100),
  email: z.string().trim().email("Please enter a valid email address"),
  phone: z.string().trim().max(30).regex(/^[+()\d\s-]*$/, "Use numbers, spaces, + or - only").optional().or(z.literal("")),
  role: z.enum(["parent", "student", "homeschool", "other"], { errorMap: () => ({ message: "Tell us who you are" }) }),
  year_level: z.string().min(1, "Choose a year level"),
  interests: z.array(z.string()).min(1, "Pick at least one course"),
  learning_mode: z.string().min(1, "Choose how you'd like to learn"),
  message: z.string().trim().max(2000, "Please keep it under 2000 characters").optional(),
  consent: z.literal(true, { errorMap: () => ({ message: "Please agree so we can reply to you" }) }),
});

const defaults = { name: "", email: "", phone: "", role: undefined, year_level: "", interests: [], learning_mode: "", message: "", consent: false };
const inputCls = "h-12 rounded-2xl border-2 border-ink/15 bg-white px-4 text-base focus-visible:ring-2 focus-visible:ring-ink focus-visible:border-ink";

const SelectField = ({ form, name, label, placeholder, options, testid }) => (
  <FormField control={form.control} name={name} render={({ field }) => (
    <FormItem>
      <FormLabel className="font-bold text-ink">{label}</FormLabel>
      <Select onValueChange={field.onChange} value={field.value}>
        <FormControl>
          <SelectTrigger className={inputCls} data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        </FormControl>
        <SelectContent className="rounded-2xl">
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} data-testid={`${testid}-${o.value}`}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <FormMessage />
    </FormItem>
  )} />
);

const Success = ({ onReset }) => (
  <div className="flex flex-col items-start gap-4 py-8" role="status" data-testid="enquiry-success">
    <CheckCircle2 className="h-12 w-12 text-ink" aria-hidden="true" />
    <h3 className="text-3xl font-semibold">Thanks, your enquiry is in!</h3>
    <p className="text-ink-soft text-lg">We'll be in touch by email soon to talk about the right next step.</p>
    <button type="button" onClick={onReset} className="rounded-full border border-ink/10 px-5 py-2.5 font-bold hover:bg-cream transition-colors" data-testid="enquiry-send-another">
      Send another enquiry
    </button>
  </div>
);

export const EnquiryForm = () => {
  const [done, setDone] = useState(false);
  const form = useForm({ resolver: zodResolver(schema), defaultValues: defaults, mode: "onTouched" });
  const { isSubmitting } = form.formState;

  const onSubmit = async (v) => {
    try {
      await axios.post(`${API}/enquiries`, { ...v, year_level: Number(v.year_level), phone: v.phone || null, message: v.message || null });
      setDone(true);
      form.reset(defaults);
    } catch (e) {
      toast.error("Sorry, we couldn't send that. Please try again.");
    }
  };

  if (done) return <Success onReset={() => setDone(false)} />;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="grid gap-5 sm:grid-cols-2" data-testid="enquiry-form" aria-label="Enquiry form">
        <FormField control={form.control} name="name" render={({ field }) => (
          <FormItem>
            <FormLabel className="font-bold text-ink">Your name</FormLabel>
            <FormControl><Input autoComplete="name" className={inputCls} data-testid="enquiry-name-input" {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />
        <FormField control={form.control} name="email" render={({ field }) => (
          <FormItem>
            <FormLabel className="font-bold text-ink">Email</FormLabel>
            <FormControl><Input type="email" autoComplete="email" className={inputCls} data-testid="enquiry-email-input" {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />
        <FormField control={form.control} name="phone" render={({ field }) => (
          <FormItem>
            <FormLabel className="font-bold text-ink">Phone <span className="font-normal text-ink-muted">(optional)</span></FormLabel>
            <FormControl><Input type="tel" autoComplete="tel" className={inputCls} data-testid="enquiry-phone-input" {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />
        <SelectField form={form} name="role" label="I am a…" placeholder="Choose one" options={ROLES} testid="enquiry-role-select" />
        <SelectField form={form} name="year_level" label="Student's year level" placeholder="Year 5 – Year 10"
          options={YEAR_LEVELS.map((y) => ({ value: String(y), label: `Year ${y}` }))} testid="enquiry-year-select" />
        <SelectField form={form} name="learning_mode" label="Preferred way to learn" placeholder="Choose one" options={LEARNING_MODES} testid="enquiry-mode-select" />

        <FormField control={form.control} name="interests" render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <fieldset>
              <legend className="text-sm font-bold text-ink mb-3">Courses you're interested in</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                {COURSES.map((c) => {
                  const checked = field.value.includes(c.slug);
                  return (
                    <label key={c.slug} className={`flex cursor-pointer items-center gap-3 rounded-2xl border-2 px-4 py-3 font-semibold transition-colors ${checked ? `${c.color} border-ink` : "border-ink/15 bg-white hover:border-ink/40"}`}>
                      <Checkbox checked={checked} data-testid={`enquiry-interest-${c.slug}`}
                        onCheckedChange={(on) => field.onChange(on ? [...field.value, c.slug] : field.value.filter((s) => s !== c.slug))} />
                      {c.title}
                    </label>
                  );
                })}
              </div>
            </fieldset>
            <FormMessage />
          </FormItem>
        )} />

        <FormField control={form.control} name="message" render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel className="font-bold text-ink">Anything you'd like us to know? <span className="font-normal text-ink-muted">(optional)</span></FormLabel>
            <FormControl><Textarea rows={4} className="rounded-2xl border-2 border-ink/15 bg-white px-4 py-3 text-base focus-visible:ring-2 focus-visible:ring-ink" data-testid="enquiry-message-input" {...field} /></FormControl>
            <FormMessage />
          </FormItem>
        )} />

        <FormField control={form.control} name="consent" render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <div className="flex items-start gap-3">
              <FormControl><Checkbox checked={field.value} onCheckedChange={(v) => field.onChange(v === true)} className="mt-1" data-testid="enquiry-consent-checkbox" /></FormControl>
              <FormLabel className="font-normal leading-relaxed text-ink-soft">I agree that MUCCI Academy can use these details to reply to my enquiry.</FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )} />

        <div className="sm:col-span-2">
          <button type="submit" disabled={isSubmitting} data-testid="enquiry-submit-button"
            className="inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-base font-bold text-cream transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-ink-soft disabled:opacity-70">
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
            {isSubmitting ? "Sending…" : "Send enquiry"}
          </button>
        </div>
      </form>
    </Form>
  );
};
